<template>
  <Teleport to="body">
    <!-- Backdrop — subtle dim + blur, not the headline element. The
         clip-path spawn carries the spectacle; the backdrop just frames
         the focus. Pointer events guarded so the closed state can't
         eat clicks on shop content. -->
    <Transition name="qd-backdrop">
      <div
        v-if="drawerOpen"
        class="qd-backdrop"
        @click="closeDrawer"
      ></div>
    </Transition>

    <!-- Light beam — a single bright vertical line that shoots from
         the queue button up toward the spawning container. Lives ~120
         ms, exits before the container fully materializes. Acts as the
         "tether" between the trigger and the panel. -->
    <Transition name="qd-beam">
      <div
        v-if="drawerOpen"
        class="qd-beam"
        :style="beamStyle"
        aria-hidden="true"
      ></div>
    </Transition>

    <!-- Particle burst — eight particles fan out from the button
         origin radially. Each particle has a randomised final position
         baked into a CSS variable so the burst doesn't look like a
         symmetric flower. -->
    <Transition name="qd-burst">
      <div
        v-if="drawerOpen"
        class="qd-burst"
        :style="burstHostStyle"
        aria-hidden="true"
      >
        <span
          v-for="(p, i) in BURST_PARTICLES"
          :key="i"
          class="qd-burst-p"
          :style="particleStyle(i, p)"
        ></span>
      </div>
    </Transition>

    <!-- The container itself — spawned ABOVE the player, anchored at
         the player's max-width and bottom edge. The clip-path circle
         expands from the queue button's coordinates outward, while
         a perspective rotateX(-12deg)→0 + scale(0.75)→1 entrance reads
         as the panel popping forward from below. -->
    <Transition name="qd-panel" @after-leave="onPanelClosed">
      <aside
        v-if="drawerOpen"
        class="qd-panel"
        :style="panelStyle"
        aria-label="Queue, history, and playlists"
      >
        <!-- Inner glow ring that paints during the spawn (clip-path
             reveals it from inside-out) — a one-shot light flash
             keyed to the cover accent. -->
        <span class="qd-spawn-flash" aria-hidden="true"></span>
        <!-- HUD corner brackets — rim-idle, cover-accent tinted. Purely
             decorative, matching the player's W0 bracket treatment. -->
        <span class="qd-bracket qd-bracket--tr" aria-hidden="true"></span>
        <span class="qd-bracket qd-bracket--bl" aria-hidden="true"></span>

        <header class="qd-head">
          <div class="qd-tabs" role="tablist">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              role="tab"
              class="qd-tab"
              :class="{ active: drawerTab === tab.key }"
              :aria-selected="drawerTab === tab.key"
              @click="setTab(tab.key)"
            >
              <NuxtIcon :name="tab.icon" />
              <span>{{ tab.label }}</span>
              <span v-if="tab.count !== null" class="qd-tab-count">{{ tab.count }}</span>
              <span class="qd-tab-underline" :class="{ visible: drawerTab === tab.key }"></span>
            </button>
          </div>
          <button type="button" class="qd-close" @click="closeDrawer" aria-label="Close">
            <NuxtIcon name="mdi:close" />
          </button>
        </header>

        <div class="qd-body">
          <Transition :name="`qd-tab-${tabDirection}`" mode="out-in">
            <!-- ── Queue ───────────────────────────────────────── -->
            <section v-if="drawerTab === 'queue'" key="queue" class="qd-section">
              <div v-if="queue.length === 0" class="qd-empty">
                <NuxtIcon name="mdi:playlist-music-outline" class="qd-empty-icon" />
                <h3>Queue is empty</h3>
                <p>Open a track's "···" menu and choose <strong>Add to queue</strong>.</p>
              </div>
              <TransitionGroup v-else name="qd-row" tag="ol" class="qd-rows">
                <li
                  v-for="(track, idx) in queue"
                  :key="track.id || idx"
                  class="qd-row"
                  :class="{ 'is-current': currentTrack && currentTrack.id === track.id }"
                  :style="{ '--row-idx': idx }"
                >
                  <button
                    type="button"
                    class="qd-row-art"
                    :aria-label="`Play ${track.title}`"
                    @click="playFromQueue(idx)"
                  >
                    <img v-if="track.imageUrl" :src="artSrc(track.imageUrl)" :alt="track.title" />
                    <span v-else class="qd-row-art-fallback">
                      <NuxtIcon name="mdi:music-note" />
                    </span>
                    <span class="qd-row-art-play">
                      <NuxtIcon name="mdi:play" />
                    </span>
                  </button>
                  <div class="qd-row-meta">
                    <div class="qd-row-title">{{ track.title }}</div>
                    <div class="qd-row-sub">Up next · {{ idx + 1 }}</div>
                  </div>
                  <div class="qd-row-actions">
                    <button
                      type="button"
                      class="qd-row-act"
                      :disabled="idx === 0"
                      :aria-label="'Move up'"
                      @click="moveInQueue(idx, idx - 1)"
                    >
                      <NuxtIcon name="mdi:arrow-up" />
                    </button>
                    <button
                      type="button"
                      class="qd-row-act"
                      :disabled="idx === queue.length - 1"
                      :aria-label="'Move down'"
                      @click="moveInQueue(idx, idx + 1)"
                    >
                      <NuxtIcon name="mdi:arrow-down" />
                    </button>
                    <button
                      type="button"
                      class="qd-row-act"
                      :aria-label="'Remove from queue'"
                      @click="removeFromQueue(idx)"
                    >
                      <NuxtIcon name="mdi:close" />
                    </button>
                  </div>
                </li>
              </TransitionGroup>
              <div v-if="queue.length > 0" class="qd-section-foot">
                <button type="button" class="qd-section-btn" @click="clearQueue">
                  <NuxtIcon name="mdi:delete-sweep-outline" />
                  Clear queue
                </button>
              </div>
            </section>

            <!-- ── History ─────────────────────────────────────── -->
            <section v-else-if="drawerTab === 'history'" key="history" class="qd-section">
              <div v-if="history.length === 0" class="qd-empty">
                <NuxtIcon name="mdi:history" class="qd-empty-icon" />
                <h3>No history yet</h3>
                <p>Tracks you play will appear here, newest first.</p>
              </div>
              <template v-else>
                <div class="qd-section-bar">
                  <span class="qd-section-count">{{ history.length }} recently played</span>
                  <button
                    type="button"
                    class="qd-section-action"
                    :disabled="clearingHistory"
                    @click="onClearHistory"
                  >
                    <NuxtIcon name="mdi:delete-sweep-outline" />
                    {{ clearingHistory ? 'Clearing…' : 'Clear history' }}
                  </button>
                </div>
                <TransitionGroup name="qd-row" tag="ol" class="qd-rows">
                <li
                  v-for="(entry, idx) in history"
                  :key="entry._id"
                  class="qd-row qd-row--history"
                  :style="{ '--row-idx': idx }"
                >
                  <button
                    type="button"
                    class="qd-row-art"
                    :aria-label="`Replay ${entry.track.title}`"
                    @click="replayHistory(entry)"
                  >
                    <img v-if="entry.track.imageUrl" :src="artSrc(entry.track.imageUrl)" :alt="entry.track.title" />
                    <span v-else class="qd-row-art-fallback">
                      <NuxtIcon name="mdi:music-note" />
                    </span>
                    <span class="qd-row-art-play">
                      <NuxtIcon name="mdi:replay" />
                    </span>
                  </button>
                  <div class="qd-row-meta">
                    <div class="qd-row-title">{{ entry.track.title }}</div>
                    <div class="qd-row-sub">
                      <span>{{ formatRelative(entry.playedAt) }}</span>
                      <span class="qd-row-source">· {{ entry.source }}</span>
                    </div>
                  </div>
                </li>
                </TransitionGroup>
              </template>
            </section>

            <!-- ── Playlists ───────────────────────────────────── -->
            <section v-else key="playlists" class="qd-section qd-section--playlists">
              <!-- A playlist belongs to an account: every /api/shop/playlists
                   route answers 401 without a session. The form used to render
                   for everyone and invite you to "create your first one", and
                   the POST it fired failed silently — the button simply did
                   nothing, forever. Say what is actually required instead. -->
              <div v-if="!loggedIn" class="qd-empty qd-empty--compact">
                <NuxtIcon name="mdi:playlist-plus" class="qd-empty-icon" />
                <h3>{{ $t('shop.playlists_signed_out_title') }}</h3>
                <p>{{ $t('shop.playlists_signed_out_body') }}</p>
                <a :href="loginUrl" class="qd-pl-signin">
                  <NuxtIcon name="mdi:login" />
                  {{ $t('shop.playlists_signed_out_cta') }}
                </a>
              </div>

              <div v-if="loggedIn" class="qd-pl-toolbar">
                <input
                  v-model="newPlaylistName"
                  type="text"
                  class="qd-pl-input"
                  placeholder="New playlist name…"
                  maxlength="80"
                  @keyup.enter="createInline"
                />
                <button
                  type="button"
                  class="qd-pl-create"
                  :disabled="!newPlaylistName.trim() || creating"
                  @click="createInline"
                >
                  <NuxtIcon :name="creating ? 'mdi:loading' : 'mdi:plus'" :class="{ 'spin': creating }" />
                  Create
                </button>
              </div>
              <div v-if="loggedIn && playlists.length > 0" class="qd-section-bar">
                <span class="qd-section-count">{{ playlists.length }} playlist{{ playlists.length === 1 ? '' : 's' }}</span>
                <NuxtLink to="/playlists" class="qd-section-action" @click="closeDrawer">
                  <NuxtIcon name="mdi:open-in-new" />
                  See all
                </NuxtLink>
              </div>
              <div v-else-if="loggedIn && playlists.length === 0" class="qd-empty qd-empty--compact">
                <NuxtIcon name="mdi:playlist-plus" class="qd-empty-icon" />
                <h3>No playlists yet</h3>
                <p>Type a name above to create your first one.</p>
              </div>
              <TransitionGroup v-else-if="loggedIn" name="qd-card" tag="div" class="qd-pl-grid">
                <article
                  v-for="(pl, idx) in playlists"
                  :key="pl._id"
                  class="qd-pl-card"
                  :style="{ ...paletteStyle(pl.coverPalette), '--row-idx': idx }"
                  @click="openPlaylist(pl)"
                >
                  <div class="qd-pl-cover">
                    <TransitionGroup name="qd-tile" tag="div" class="qd-pl-grid-4">
                      <span
                        v-for="(c, ci) in coverTiles(pl)"
                        :key="`${pl._id}-${ci}-${c.trackId || 'blank'}`"
                        class="qd-pl-tile"
                        :class="{ 'is-blank': !c.url }"
                      >
                        <img v-if="c.url" :src="c.url" alt="" loading="lazy" />
                        <NuxtIcon v-else name="mdi:music-note" />
                      </span>
                    </TransitionGroup>
                    <span class="qd-pl-sheen" aria-hidden="true"></span>
                  </div>
                  <div class="qd-pl-meta">
                    <div class="qd-pl-title">{{ pl.name }}</div>
                    <div class="qd-pl-sub">{{ pl.trackCount }} track{{ pl.trackCount === 1 ? '' : 's' }}</div>
                  </div>
                </article>
              </TransitionGroup>
            </section>
          </Transition>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
const { $toast } = useNuxtApp()

const player = useAudioPlayer()
const {
  queue, history, playlists, drawerOpen, drawerTab, drawerOrigin, currentTrack,
  closeDrawer, moveInQueue, removeFromQueue, clearQueue, play,
  fetchHistory, clearHistory, fetchPlaylists, createPlaylist
} = player

const router = useRouter()

const tabs = computed(() => [
  { key: 'queue',     label: 'Queue',     icon: 'mdi:playlist-play',          count: queue.value.length },
  { key: 'history',   label: 'History',   icon: 'mdi:history',                count: history.value.length || null },
  { key: 'playlists', label: 'Playlists', icon: 'mdi:bookmark-music-outline', count: playlists.value.length || null }
])

const lastTabIndex = ref(0)
const tabDirection = ref('fwd')
watch(drawerTab, (newTab) => {
  const newIdx = tabs.value.findIndex(t => t.key === newTab)
  tabDirection.value = newIdx >= lastTabIndex.value ? 'fwd' : 'back'
  lastTabIndex.value = newIdx
})

const setTab = (key) => {
  drawerTab.value = key
  if (key === 'history') fetchHistory()
  if (key === 'playlists' && loggedIn.value) fetchPlaylists()
}

watch(drawerOpen, (open) => {
  if (open) {
    if (drawerTab.value === 'history' && history.value.length === 0) fetchHistory()
    if (drawerTab.value === 'playlists' && loggedIn.value && playlists.value.length === 0) fetchPlaylists()
  }
})
onMounted(() => { player.hydrateQueue?.() })

// Escape closes the panel. Capture phase so it wins over deeper
// listeners on the same key (e.g. a focused input inside the drawer
// — we still WANT escape to close, even if the user is mid-rename).
const onKeydown = (e) => {
  if (e.key === 'Escape' && drawerOpen.value) {
    e.stopPropagation()
    closeDrawer()
  }
}
onMounted(() => { document.addEventListener('keydown', onKeydown, true) })
onBeforeUnmount(() => { document.removeEventListener('keydown', onKeydown, true) })

const accentChannels = computed(() => {
  if (typeof window === 'undefined') return '0 242 255'
  const v = getComputedStyle(document.documentElement).getPropertyValue('--player-accent-channels').trim()
  return v || '0 242 255'
})

// ── Spawn geometry ───────────────────────────────────────────
// The container's clip-path circle expands from the queue button's
// centre. We compute the percentage offset of the origin within the
// panel's bounding box and feed it as a CSS variable. The keyframe
// (.qd-panel-enter) reads those vars and runs the circle outward.

// Player metrics — the panel mirrors the player's max-width and sits
// just above its top edge. The bottom anchor stays fixed; only the
// height + spawn vary by viewport.
const PLAYER_MAX_WIDTH = 1532
const PLAYER_GUTTER = 24       // shell padding
const PLAYER_BOTTOM = 18       // shell padding-bottom
const PLAYER_HEIGHT = 76
const PANEL_GAP = 16
const PANEL_BOTTOM = PLAYER_BOTTOM + PLAYER_HEIGHT + PANEL_GAP // 110px

const panelStyle = computed(() => {
  if (typeof window === 'undefined') {
    return {
      '--qd-accent-channels': accentChannels.value,
      '--qd-spawn-x': '50%',
      '--qd-spawn-y': '100%'
    }
  }
  const vw = window.innerWidth
  const panelWidth = Math.min(PLAYER_MAX_WIDTH, vw - PLAYER_GUTTER * 2)
  const panelLeft = (vw - panelWidth) / 2
  // Panel viewport top = vh - PANEL_BOTTOM - height. Height is
  // determined by CSS clamp; we use a conservative ~65vh for math, but
  // the actual height is set via CSS — the spawn-y just needs to be
  // an approximate ratio in the panel's local frame.
  const ox = drawerOrigin.value?.x ?? (vw / 2)
  const oy = drawerOrigin.value?.y ?? (window.innerHeight - PANEL_BOTTOM)
  // Convert origin → panel-local percentages. Y is relative to the
  // panel's TOP edge (the panel's top is somewhere around vh - panel
  // height - PANEL_BOTTOM). We don't need exact pixel maths because
  // clip-path circle accepts percentages and the visual error is tiny.
  const localX = ((ox - panelLeft) / panelWidth) * 100
  // Y origin: roughly at the very bottom of the panel (where the
  // queue button sits, just below the panel). Clamp generously.
  const localY = 100 + 8 // 108% — origin just OUTSIDE the panel bottom
  return {
    '--qd-accent-channels': accentChannels.value,
    '--qd-spawn-x': `${Math.max(0, Math.min(100, localX))}%`,
    '--qd-spawn-y': `${localY}%`,
    '--qd-panel-width': `${panelWidth}px`,
    '--qd-panel-left': `${panelLeft}px`
  }
})

const beamStyle = computed(() => {
  if (typeof window === 'undefined') return { display: 'none' }
  const ox = drawerOrigin.value?.x ?? (window.innerWidth / 2)
  const oy = drawerOrigin.value?.y ?? (window.innerHeight - PANEL_BOTTOM)
  // The beam reaches from the button origin up to the panel's bottom
  // edge (which sits at viewport-y = innerHeight - PANEL_BOTTOM).
  const beamHeight = Math.max(0, oy - (window.innerHeight - PANEL_BOTTOM))
  return {
    left: `${ox}px`,
    top: `${window.innerHeight - PANEL_BOTTOM}px`,
    height: `${beamHeight}px`,
    '--qd-accent-channels': accentChannels.value
  }
})

const BURST_PARTICLES = Array.from({ length: 12 }, (_, i) => {
  // Each particle gets a precomputed angle + travel distance so the
  // burst is irregular instead of a uniform asterisk. Seeded by index
  // so the layout is stable across re-mounts (no jitter on reopen).
  const baseAngle = (i / 12) * 360
  const jitter = ((i * 73) % 30) - 15 // -15..+15 deg
  const distance = 60 + ((i * 13) % 80) // 60..140 px
  return {
    angle: baseAngle + jitter,
    distance
  }
})

const burstHostStyle = computed(() => {
  if (typeof window === 'undefined') return { display: 'none' }
  const ox = drawerOrigin.value?.x ?? (window.innerWidth / 2)
  const oy = drawerOrigin.value?.y ?? (window.innerHeight - PANEL_BOTTOM)
  return {
    left: `${ox}px`,
    top: `${oy}px`,
    '--qd-accent-channels': accentChannels.value
  }
})

const particleStyle = (i, p) => ({
  '--p-angle': `${p.angle}deg`,
  '--p-distance': `${p.distance}px`,
  '--p-delay': `${(i % 4) * 18}ms`
})

const playFromQueue = (idx) => {
  const t = queue.value[idx]
  if (!t) return
  removeFromQueue(idx)
  play(t, { source: 'queue' })
}
const clearingHistory = ref(false)
const onClearHistory = async () => {
  if (clearingHistory.value) return
  const ok = await confirmDanger(
    'Clear play history?',
    'Every track you have played will be removed from your history. This cannot be undone.',
    'Yes, clear it',
    'storefront'
  )
  if (!ok) return
  clearingHistory.value = true
  try {
    const deleted = await clearHistory()
    $toast.success(deleted > 0 ? `Cleared ${deleted} play${deleted === 1 ? '' : 's'}` : 'History cleared')
  } catch (err) {
    console.error('[queue-drawer] clearing history failed', err)
    $toast.error('Could not clear your history. Please try again.')
  } finally {
    clearingHistory.value = false
  }
}

const replayHistory = (entry) => {
  if (!entry?.track) return
  play(entry.track, { source: 'history' })
}
const openPlaylist = (pl) => {
  closeDrawer()
  // No `/shop` prefix: on the shop subdomain router.options.ts strips it from
  // every route, so the detail page lives at /playlists/:id. Pushing the
  // prefixed path matched nothing and the row simply did not navigate.
  router.push(`/playlists/${pl._id}`)
}
const { t } = useI18n()
const { loggedIn } = useUserSession()
// The shop subdomain owns the account pages, so this is the same
// cross-subdomain hop the mini-cart makes — a plain href, because a
// router push cannot leave this route tree.
const loginUrl = computed(() => getSubdomainUrl('shop', '/login'))

const newPlaylistName = ref('')
const creating = ref(false)
const createInline = async () => {
  const name = newPlaylistName.value.trim()
  if (!name || creating.value) return
  creating.value = true
  try {
    await createPlaylist({ name })
    newPlaylistName.value = ''
  } catch (err) {
    // Was console-only: the button spun, nothing happened, and the
    // reader had no way to tell whether it had worked.
    console.error('[QueueDrawer] createPlaylist failed', err)
    useNuxtApp().$toast?.error?.(
      err?.statusCode === 401 || err?.status === 401
        ? t('shop.playlists_signed_out_title')
        : t('shop.playlists_create_failed')
    )
  } finally {
    creating.value = false
  }
}

const coverTiles = (pl) => {
  const tiles = (pl.covers || []).slice(0, 4).map(c => ({
    trackId: c.trackId,
    url: artworkUrl(c.artwork)
  }))
  while (tiles.length < 4) tiles.push({ trackId: null, url: null })
  return tiles
}
/**
 * Queue and history rows carry whatever `imageUrl` the page that pushed them
 * built, and not every page proxies it. MasterPlayer wraps the same value at
 * render time, so an unproxied track showed a cover in the player and a blank
 * tile in this drawer. Idempotent — an already-proxied relative path passes
 * through untouched.
 */
function artSrc(url) {
  if (!url || typeof url !== 'string') return ''
  if (!url.startsWith('http')) return url
  return `/api/assets/serve?url=${encodeURIComponent(url.split('?')[0])}`
}

function artworkUrl(art) {
  if (!art) return null
  const raw = art?.low?.url || art?.thumbnail?.url || art?.original?.url || art?.url || null
  if (!raw) return null
  if (raw.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(raw)}`
  return raw
}
const paletteStyle = (palette) => {
  if (!palette) return {}
  return { '--qd-card-accent': palette }
}

const formatRelative = (iso) => {
  if (!iso) return ''
  const d = typeof iso === 'string' ? new Date(iso) : iso
  const diff = Date.now() - d.getTime()
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  return `${day}d ago`
}

const onPanelClosed = () => {
  // No-op hook for now; reserved if we want to clear local state on
  // exit. Vue's Transition fires this after the leave animation ends.
}
</script>

<style scoped>
/* ───────────────────────────────────────────────────────────────
   Backdrop + supporting effects
─────────────────────────────────────────────────────────────── */
.qd-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 3, 7, 0.45);
  backdrop-filter: blur(3px) saturate(1.1);
  -webkit-backdrop-filter: blur(3px) saturate(1.1);
  z-index: 10000;
}

/* Light beam — a thin vertical bar that flashes between the queue
   button and the spawning panel. Lives ~180ms, then dissolves. */
.qd-beam {
  position: fixed;
  width: 2px;
  transform: translateX(-50%);
  background: linear-gradient(
    to top,
    rgb(var(--qd-accent-channels, 0 242 255) / 0) 0%,
    rgb(var(--qd-accent-channels, 0 242 255) / 0.65) 35%,
    rgb(var(--qd-accent-channels, 0 242 255) / 0.95) 65%,
    rgb(var(--qd-accent-channels, 0 242 255) / 0) 100%
  );
  filter: blur(0.5px) drop-shadow(0 0 8px rgb(var(--qd-accent-channels, 0 242 255) / 0.6));
  z-index: 10002;
  pointer-events: none;
  border-radius: 2px;
}
.qd-beam-enter-active {
  transition: opacity 80ms ease-out, transform 180ms cubic-bezier(0.22, 1.2, 0.36, 1);
  animation: qd-beam-flash 260ms ease-out forwards;
}
.qd-beam-leave-active { transition: opacity 100ms ease; }
.qd-beam-enter-from {
  opacity: 0;
  transform: translateX(-50%) scaleY(0);
  transform-origin: bottom center;
}
.qd-beam-leave-to { opacity: 0; }
@keyframes qd-beam-flash {
  0%   { opacity: 0; transform: translateX(-50%) scaleY(0); transform-origin: bottom; }
  35%  { opacity: 1; transform: translateX(-50%) scaleY(1); }
  100% { opacity: 0; transform: translateX(-50%) scaleY(1); }
}

/* Particle burst — 12 particles fan outward from the button origin.
   Each particle has its own --p-angle / --p-distance / --p-delay set
   inline so the spray reads as irregular. */
.qd-burst {
  position: fixed;
  width: 0;
  height: 0;
  z-index: 10003;
  pointer-events: none;
}
.qd-burst-enter-active { transition: opacity 60ms ease-out; }
.qd-burst-leave-active { transition: opacity 100ms ease; }
.qd-burst-enter-from, .qd-burst-leave-to { opacity: 0; }

.qd-burst-p {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 6px;
  margin: -3px 0 0 -3px;
  border-radius: 50%;
  background: rgb(var(--qd-accent-channels, 0 242 255));
  box-shadow:
    0 0 8px rgb(var(--qd-accent-channels, 0 242 255) / 0.85),
    0 0 14px rgb(var(--qd-accent-channels, 0 242 255) / 0.5);
  opacity: 0;
  animation: qd-burst-fly 620ms cubic-bezier(0.18, 0.7, 0.2, 1) forwards;
  animation-delay: var(--p-delay, 0ms);
}
@keyframes qd-burst-fly {
  0%   {
    opacity: 0;
    transform: rotate(var(--p-angle, 0deg)) translate(0) scale(0.4);
  }
  18%  {
    opacity: 1;
    transform: rotate(var(--p-angle, 0deg)) translate(calc(var(--p-distance, 80px) * 0.18)) scale(1);
  }
  100% {
    opacity: 0;
    transform: rotate(var(--p-angle, 0deg)) translate(var(--p-distance, 80px)) scale(0.6);
  }
}

/* ───────────────────────────────────────────────────────────────
   Panel — spawns ABOVE the player from the queue button origin
─────────────────────────────────────────────────────────────── */
.qd-panel {
  position: fixed;
  left: var(--qd-panel-left, 50%);
  width: var(--qd-panel-width, min(1532px, calc(100vw - 48px)));
  bottom: 110px; /* player bottom 18 + height 76 + gap 16 */
  height: clamp(360px, 65vh, 720px);
  z-index: 10001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  /* Same dark-gradient lane as the player body so the two read as a
     single connected surface, with the cover-accent border lighting
     up the seam between them. */
  background:
    linear-gradient(180deg,
      rgba(15, 17, 26, 0.97) 0%,
      rgba(8, 9, 14, 0.99) 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow:
    0 -2px 0 0 rgb(var(--qd-accent-channels, 0 242 255) / 0.2) inset,
    0 30px 70px -16px rgba(0, 0, 0, 0.85),
    0 0 0 1px rgba(255, 255, 255, 0.04);
  color: var(--text-main, #e8eaf2);
  transform-style: preserve-3d;
  perspective: 1200px;
}
/* Gradient ring around the panel — keyed to the cover accent, matches
   the player's ::before treatment so the two frames feel unified. */
.qd-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1.5px;
  background: linear-gradient(
    160deg,
    rgb(var(--qd-accent-channels, 0 242 255) / 0.7),
    rgba(255, 255, 255, 0.16) 35%,
    rgba(255, 255, 255, 0.04) 70%,
    rgb(var(--qd-accent-channels, 0 242 255) / 0.45)
  );
  -webkit-mask:
    linear-gradient(#000, #000) content-box,
    linear-gradient(#000, #000);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  pointer-events: none;
}
/* Inner radial that lights the corner above the button origin —
   complements the smoke + glow lane below. */
.qd-panel::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: radial-gradient(
    60% 80% at var(--qd-spawn-x, 50%) 100%,
    rgb(var(--qd-accent-channels, 0 242 255) / 0.16) 0%,
    transparent 65%
  );
  opacity: 0.9;
}
/* One-shot flash that lights the panel interior during the spawn —
   fades to nothing within ~600 ms so the panel settles to its calm
   resting tint. Sits behind everything else inside the panel. */
.qd-spawn-flash {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background: radial-gradient(
    closest-side at var(--qd-spawn-x, 50%) var(--qd-spawn-y, 100%),
    rgb(var(--qd-accent-channels, 0 242 255) / 0.55) 0%,
    rgb(var(--qd-accent-channels, 0 242 255) / 0.18) 30%,
    transparent 60%
  );
  opacity: 0;
  animation: qd-spawn-flash 700ms ease-out forwards;
  animation-delay: 60ms;
}
/* ── HUD corner brackets ── */
.qd-bracket {
  position: absolute;
  width: 14px;
  height: 14px;
  z-index: 3;
  pointer-events: none;
  border: 0 solid rgb(var(--qd-accent-channels, var(--primary-channels, 0 242 255)) / 0.55);
}
.qd-bracket--tr { top: 6px; right: 6px; border-top-width: 2px; border-right-width: 2px; }
.qd-bracket--bl { bottom: 6px; left: 6px; border-bottom-width: 2px; border-left-width: 2px; }

@keyframes qd-spawn-flash {
  0%   { opacity: 0; transform: scale(0.4); }
  20%  { opacity: 1; }
  100% { opacity: 0; transform: scale(1.4); }
}

/* ── Panel transition: the headline animation ─────────────── */
.qd-panel-enter-active {
  transition:
    clip-path 520ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 540ms cubic-bezier(0.22, 1.2, 0.36, 1),
    opacity 240ms ease;
}
.qd-panel-leave-active {
  transition:
    clip-path 340ms cubic-bezier(0.45, 0, 0.55, 1),
    transform 320ms cubic-bezier(0.5, 0, 0.6, 1),
    opacity 220ms ease;
}
.qd-panel-enter-from {
  opacity: 0;
  clip-path: circle(0% at var(--qd-spawn-x, 50%) var(--qd-spawn-y, 100%));
  transform: perspective(1200px) translateY(36px) scale(0.7) rotateX(-14deg);
  transform-origin: var(--qd-spawn-x, 50%) var(--qd-spawn-y, 100%);
}
.qd-panel-enter-to {
  opacity: 1;
  clip-path: circle(150% at var(--qd-spawn-x, 50%) var(--qd-spawn-y, 100%));
  transform: perspective(1200px) translateY(0) scale(1) rotateX(0);
  transform-origin: var(--qd-spawn-x, 50%) var(--qd-spawn-y, 100%);
}
.qd-panel-leave-from {
  opacity: 1;
  clip-path: circle(150% at var(--qd-spawn-x, 50%) var(--qd-spawn-y, 100%));
  transform: perspective(1200px) translateY(0) scale(1) rotateX(0);
}
.qd-panel-leave-to {
  opacity: 0;
  clip-path: circle(0% at var(--qd-spawn-x, 50%) var(--qd-spawn-y, 100%));
  transform: perspective(1200px) translateY(24px) scale(0.78) rotateX(-10deg);
  transform-origin: var(--qd-spawn-x, 50%) var(--qd-spawn-y, 100%);
}

/* Backdrop transition */
.qd-backdrop-enter-active, .qd-backdrop-leave-active {
  transition: opacity 0.32s ease, backdrop-filter 0.32s ease;
}
.qd-backdrop-enter-from, .qd-backdrop-leave-to { opacity: 0; }

/* ───────────────────────────────────────────────────────────────
   Header / tabs / body
─────────────────────────────────────────────────────────────── */
.qd-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 0;
  position: relative;
  z-index: 2;
  /* Header rises into place once the clip-path is mostly open. */
  animation: qd-content-rise 600ms cubic-bezier(0.22, 1.2, 0.36, 1) both;
  animation-delay: 180ms;
}
.qd-tabs {
  display: flex;
  gap: 4px;
  align-items: stretch;
}
.qd-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  font-family: var(--cy-display);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 4px 4px 0 0;
  transition: color 0.18s ease, background 0.18s ease;
}
.qd-tab:hover { color: rgba(255, 255, 255, 0.85); }
.qd-tab.active { color: var(--text-main, #e8eaf2); }
.qd-tab-count {
  font-family: var(--cy-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
}
.qd-tab.active .qd-tab-count {
  background: rgb(var(--qd-accent-channels, 0 242 255) / 0.22);
  color: rgb(var(--qd-accent-channels, 0 242 255));
}
.qd-tab-underline {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 0;
  height: 2px;
  border-radius: 2px;
  background: rgb(var(--qd-accent-channels, 0 242 255));
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.4s cubic-bezier(0.22, 1.2, 0.36, 1);
}
.qd-tab-underline.visible { transform: scaleX(1); }
.qd-close {
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s ease, color 0.18s ease;
}
.qd-close:hover {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.95);
}

.qd-body {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.18) transparent;
  /* Body fades in slightly later than the header so the content reads
     as "the panel arrived first, content materialised second". */
  animation: qd-content-rise 600ms cubic-bezier(0.22, 1.2, 0.36, 1) both;
  animation-delay: 240ms;
}
.qd-body::-webkit-scrollbar { width: 6px; }
.qd-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 3px;
}
.qd-section {
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
/* Header strip above the history list: a count on the left, the destructive
   action pushed to the right so it is never the thing a thumb lands on first. */
.qd-section-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.qd-section-count {
  font-family: var(--cy-mono);
  font-size: 0.64rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.45);
}
.qd-section-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  cursor: pointer;
  transition: color .15s, border-color .15s, background .15s;
}
.qd-section-action:hover:not(:disabled) {
  color: var(--cy-alarm);
  border-color: var(--cy-alarm-dim);
  background: color-mix(in srgb, var(--accent) 8%, transparent);
}
.qd-section-action:disabled {
  opacity: .55;
  cursor: default;
}

.qd-section-foot {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}
.qd-section-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  color: rgba(255, 255, 255, 0.78);
  font-family: var(--cy-mono);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}
.qd-section-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.95);
}

@keyframes qd-content-rise {
  0%   { opacity: 0; transform: translateY(16px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* ───────────────────────────────────────────────────────────────
   Empty states + rows + cards
─────────────────────────────────────────────────────────────── */
.qd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 36px 16px;
  text-align: center;
}
.qd-empty--compact { padding: 18px 16px 6px; }
.qd-empty-icon {
  font-size: 36px;
  color: rgba(255, 255, 255, 0.32);
}
.qd-empty h3 {
  margin: 4px 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
}
.qd-empty p {
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 320px;
}

.qd-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style: none;
  padding: 0;
  margin: 0;
}
.qd-row {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 6px 8px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
  /* Stagger each row in during the spawn — index baked from template
     via inline --row-idx, multiplied by 35ms per row, capped via min(). */
  animation: qd-row-rise 480ms cubic-bezier(0.22, 1.2, 0.36, 1) both;
  animation-delay: calc(280ms + min(var(--row-idx, 0) * 35ms, 700ms));
}
@keyframes qd-row-rise {
  0%   { opacity: 0; transform: translateY(20px) scale(0.96); filter: blur(4px); }
  60%  { filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
}
.qd-row:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  transform: translateX(-2px);
}
.qd-row.is-current {
  border-color: rgb(var(--qd-accent-channels, 0 242 255) / 0.55);
  background: rgb(var(--qd-accent-channels, 0 242 255) / 0.1);
}
.qd-row-art {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  border: 0;
  cursor: pointer;
  padding: 0;
}
.qd-row-art img,
.qd-row-art-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: cover;
  color: rgba(255, 255, 255, 0.4);
}
.qd-row-art-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 18px;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.qd-row-art:hover .qd-row-art-play { opacity: 1; }
.qd-row-meta { min-width: 0; }
.qd-row-title {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text-main, #e8eaf2);
}
.qd-row-sub {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  gap: 4px;
}
.qd-row-source { text-transform: capitalize; }
.qd-row-actions {
  display: flex;
  gap: 2px;
}
.qd-row-act {
  width: 26px;
  height: 26px;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s ease, color 0.18s ease;
}
.qd-row-act:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.95);
}
.qd-row-act:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Playlist tab ──────────────────────────────────────────── */
.qd-pl-signin {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 14px;
  min-height: 36px;
  background: var(--primary);
  color: var(--cy-ground, #0b0c10);
  font-family: var(--cy-display, sans-serif);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  transition: filter 0.15s;
}
.qd-pl-signin:hover { filter: brightness(1.1); }
@media (pointer: coarse) { .qd-pl-signin { min-height: 44px; } }

.qd-pl-toolbar {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 4px;
}
.qd-pl-input {
  height: 38px;
  padding: 0 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: var(--text-main, #e8eaf2);
  font-size: 13px;
  outline: none;
  transition: border-color 0.18s ease, background 0.18s ease;
}
.qd-pl-input:focus {
  border-color: rgb(var(--qd-accent-channels, 0 242 255) / 0.55);
  background: rgba(255, 255, 255, 0.06);
}
.qd-pl-create {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 38px;
  border: 0;
  border-radius: 10px;
  background: rgb(var(--qd-accent-channels, 0 242 255) / 0.22);
  color: rgb(var(--qd-accent-channels, 0 242 255));
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.18s ease;
}
.qd-pl-create:hover:not(:disabled) { background: rgb(var(--qd-accent-channels, 0 242 255) / 0.32); }
.qd-pl-create:disabled { opacity: 0.4; cursor: not-allowed; }

.qd-pl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
}
.qd-pl-card {
  position: relative;
  padding: 10px;
  border-radius: 14px;
  background:
    linear-gradient(135deg,
      rgba(255, 255, 255, 0.03) 0%,
      rgba(255, 255, 255, 0.01) 100%);
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  overflow: hidden;
  isolation: isolate;
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  /* Cards rise + scale + tilt in slightly. Stagger by index. */
  animation: qd-card-rise 600ms cubic-bezier(0.22, 1.2, 0.36, 1) both;
  animation-delay: calc(320ms + min(var(--row-idx, 0) * 45ms, 700ms));
}
@keyframes qd-card-rise {
  0%   {
    opacity: 0;
    transform: perspective(800px) translateY(28px) rotateX(-12deg) scale(0.9);
    filter: blur(6px);
  }
  60%  { filter: blur(0); }
  100% {
    opacity: 1;
    transform: perspective(800px) translateY(0) rotateX(0) scale(1);
    filter: blur(0);
  }
}
.qd-pl-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: -1;
  background: radial-gradient(
    100% 80% at 50% 0%,
    rgb(var(--qd-card-accent, var(--qd-accent-channels, 0 242 255)) / 0.18) 0%,
    transparent 70%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}
.qd-pl-card:hover {
  transform: translateY(-3px) scale(1.015);
  border-color: rgb(var(--qd-card-accent, var(--qd-accent-channels, 0 242 255)) / 0.5);
  box-shadow: 0 22px 44px -18px rgba(0, 0, 0, 0.7);
}
.qd-pl-card:hover::before { opacity: 1; }
.qd-pl-cover {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);
  margin-bottom: 10px;
}
.qd-pl-grid-4 {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 1px;
}
.qd-pl-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  overflow: hidden;
}
.qd-pl-tile img { width: 100%; height: 100%; object-fit: cover; }
.qd-pl-tile.is-blank {
  color: rgba(255, 255, 255, 0.18);
  font-size: 22px;
  background:
    repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.02),
      rgba(255, 255, 255, 0.02) 4px,
      rgba(255, 255, 255, 0.04) 4px,
      rgba(255, 255, 255, 0.04) 8px
    );
}
.qd-pl-sheen {
  position: absolute;
  top: -40%;
  left: -50%;
  width: 60%;
  height: 200%;
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(255, 255, 255, 0.18) 50%,
    transparent 70%
  );
  transform: translateX(-30%) rotate(8deg);
  pointer-events: none;
  opacity: 0;
  transition: transform 0.7s cubic-bezier(0.2, 0.7, 0.2, 1), opacity 0.2s ease;
}
.qd-pl-card:hover .qd-pl-sheen {
  opacity: 1;
  transform: translateX(180%) rotate(8deg);
}
.qd-pl-meta { padding: 0 2px; }
.qd-pl-title {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.qd-pl-sub {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
}

/* ───────────────────────────────────────────────────────────────
   Inter-tab transitions + reorder FLIP
─────────────────────────────────────────────────────────────── */
.qd-row-enter-active {
  transition: opacity 0.35s ease, transform 0.45s cubic-bezier(0.22, 1.2, 0.36, 1);
}
.qd-row-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
  position: absolute;
  width: calc(100% - 36px);
}
.qd-row-enter-from { opacity: 0; transform: translateX(28px) scale(0.96); }
.qd-row-leave-to   { opacity: 0; transform: translateX(-32px) scale(0.94); }
.qd-row-move { transition: transform 0.45s cubic-bezier(0.22, 1.2, 0.36, 1); }

.qd-tab-fwd-enter-active, .qd-tab-back-enter-active {
  transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.22, 1.2, 0.36, 1);
}
.qd-tab-fwd-leave-active, .qd-tab-back-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.qd-tab-fwd-enter-from { opacity: 0; transform: translateX(32px); }
.qd-tab-fwd-leave-to   { opacity: 0; transform: translateX(-20px); }
.qd-tab-back-enter-from { opacity: 0; transform: translateX(-32px); }
.qd-tab-back-leave-to   { opacity: 0; transform: translateX(20px); }

.qd-tile-enter-active, .qd-tile-leave-active {
  transition: opacity 0.4s ease, transform 0.5s cubic-bezier(0.22, 1.2, 0.36, 1);
}
.qd-tile-enter-from { opacity: 0; transform: scale(1.15) rotate(2deg); }
.qd-tile-leave-to   { opacity: 0; transform: scale(0.85) rotate(-2deg); }
.qd-tile-move { transition: transform 0.4s cubic-bezier(0.22, 1.2, 0.36, 1); }

.qd-card-enter-active {
  transition: opacity 0.5s ease, transform 0.6s cubic-bezier(0.22, 1.2, 0.36, 1);
}
.qd-card-leave-active {
  transition: opacity 0.25s ease, transform 0.3s ease;
}
.qd-card-enter-from {
  opacity: 0;
  transform: translateY(24px) scale(0.92);
}
.qd-card-leave-to {
  opacity: 0;
  transform: translateY(-12px) scale(0.94);
}
.qd-card-move { transition: transform 0.45s cubic-bezier(0.22, 1.2, 0.36, 1); }

.spin { animation: qd-spin 0.9s linear infinite; }
@keyframes qd-spin { to { transform: rotate(360deg); } }

/* ───────────────────────────────────────────────────────────────
   Responsive + a11y
─────────────────────────────────────────────────────────────── */
@media (max-width: 720px) {
  .qd-panel {
    left: 12px !important;
    right: 12px;
    width: auto !important;
    bottom: 102px;
    height: clamp(320px, 60vh, 580px);
  }
  .qd-head { padding: 12px 12px 0; }
  .qd-tab { padding: 8px 10px; font-size: 12px; }
  .qd-tab-count {
    /* Tabs already display the label; the count chip turns the row into
       a horizontally-scrolling overflow on phones. Hide the chip — the
       label alone is sufficient at this width. */
    display: none;
  }
}

/* Phones (≤ 480px): the drawer fills the available viewport and the
   row layout drops to the bare three-column shape (art · meta · actions).
   The bottom offset matches the slimmer mobile player (60px) + 12px gap. */
@media (max-width: 480px) {
  .qd-panel {
    left: 8px !important;
    right: 8px;
    bottom: 80px;
    height: clamp(300px, 70vh, 560px);
    border-radius: 18px;
  }
  .qd-row {
    grid-template-columns: 40px 1fr auto;
    gap: 8px;
    padding: 5px 6px;
  }
  .qd-row-art { width: 40px; height: 40px; }
  .qd-row-title { font-size: 12px; }
  .qd-row-sub { font-size: 10px; }
  .qd-row-act { width: 28px; height: 28px; }
}

@media (prefers-reduced-motion: reduce) {
  .qd-panel-enter-active, .qd-panel-leave-active,
  .qd-backdrop-enter-active, .qd-backdrop-leave-active,
  .qd-row-enter-active, .qd-row-leave-active, .qd-row-move,
  .qd-tab-fwd-enter-active, .qd-tab-fwd-leave-active,
  .qd-tab-back-enter-active, .qd-tab-back-leave-active,
  .qd-tile-enter-active, .qd-tile-leave-active, .qd-tile-move,
  .qd-card-enter-active, .qd-card-leave-active, .qd-card-move,
  .qd-row, .qd-pl-card, .qd-head, .qd-body,
  .qd-burst-p, .qd-beam, .qd-spawn-flash,
  .qd-pl-sheen, .spin {
    transition: none !important;
    animation: none !important;
  }
  .qd-panel-enter-from, .qd-panel-leave-to {
    opacity: 0;
    clip-path: none;
    transform: none;
  }
}
</style>
